'use client'
import { Grid, Typography, styled, useMediaQuery, useTheme } from '@mui/material'
import Image from 'next/image'
import { colors } from '../../src/Theme/LegacyTheme'
import InfoIcon from '@mui/icons-material/Info'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import CardGrid from '@/UI/MUI/Card/CardGrid'
import BodyFullWidthContainer from '@/UI/BodyFullWidthContainer/BodyFullWidthContainer'
import { ButtonVariants, SingleCardDataProps } from '@/types/UI'
import TrustpilotRating from '@/components/UI/Shared/TrustpilotWidgets/TrustPilotRating'
import Link from 'next/link'
import GlobalStyles from '@app/GlobalStyles'

export const cardsData: SingleCardDataProps[] = [
  {
    title: {
      text: 'Pre-application support',
      variant: 'h3',
      component: 'h2'
    },
    description: 'Advice for customers who are considering making an application for car finance. ',
    button: {
      buttonText: 'Pre-application support articles',
      buttonLink: 'https://www.zuto.com/help-and-advice/pre-application-support/',
      buttonId: 'Pre-applicationSupportArticles',
      buttonVariant: 'contained' as ButtonVariants,
      ariaLabel: 'Navigate to Pre-application support articles'
    },
    icon: <InfoIcon sx={{ color: colors.darkGreen, fontSize: 30 }} />,
    linksList: {
      title: {
        text: 'POPULAR ARTICLES',
        component: 'h3'
      },
      items: [
        {
          text: 'What vehicles can I finance through Zuto?',
          link: 'https://www.zuto.com/help-and-advice/what-vehicles-can-i-finance/',
          target: '_self'
        },
        {
          text: 'Can I get car finance for a private car sale?',
          link: 'https://www.zuto.com/help-and-advice/private-sale-car-finance/',
          target: '_self'
        },
        {
          text: 'Difference between PCP and HP',
          link: 'https://www.zuto.com/help-and-advice/difference-between-pcp-and-hp/',
          target: '_self'
        }
      ]
    }
  },
  {
    title: {
      text: 'Your application and account',
      variant: 'h3',
      component: 'h2'
    },
    description:
      "If you've already made an application but need further help, then this is the place for you. ",
    button: {
      buttonText: 'Application and account articles',
      buttonLink: 'https://www.zuto.com/help-and-advice/your-application-and-account/',
      buttonId: 'ApplicationAndAccountArticles',
      buttonVariant: 'contained' as ButtonVariants,
      ariaLabel: 'Navigate to Application and account articles'
    },
    icon: <ManageAccountsIcon sx={{ color: colors.darkGreen, fontSize: 30 }} />,
    linksList: {
      title: {
        text: 'POPULAR ARTICLES',
        component: 'h3'
      },
      items: [
        {
          text: 'What support is available?',
          link: 'https://www.zuto.com/help-and-advice/car-finance-support/',
          target: '_self'
        },
        {
          text: 'What does pre-approved for car finance mean?',
          link: 'https://www.zuto.com/help-and-advice/pre-approved-car-finance/',
          target: '_self'
        },
        {
          text: 'What can I do in my account?',
          link: 'https://www.zuto.com/help-and-advice/what-can-i-do-in-my-account/',
          target: '_self'
        }
      ]
    }
  },
  {
    title: {
      text: 'Zuto customer service',
      variant: 'h3',
      component: 'h2'
    },
    description: 'Aftercare support for customers who have already taken out car finance.',
    button: {
      buttonText: 'Aftercare support articles',
      buttonLink: 'https://www.zuto.com/help-and-advice/zuto-customer-service/',
      buttonId: 'AftercareSupportArticles',
      buttonVariant: 'contained' as ButtonVariants,
      ariaLabel: 'Navigate to Aftercare support articles'
    },
    icon: <QuestionAnswerIcon sx={{ color: colors.darkGreen, fontSize: 30 }} />,
    linksList: {
      title: {
        text: 'POPULAR ARTICLES',
        component: 'h3'
      },
      items: [
        {
          text: 'What is voluntary termination of car finance?',
          link: 'https://www.zuto.com/help-and-advice/car-finance-voluntary-termination/',
          target: '_self'
        },
        {
          text: 'Can I pay off my car finance early?',
          link: 'https://www.zuto.com/help-and-advice/cancelling-car-finance-agreement/',
          target: '_self'
        },
        {
          text: 'Can I change my car if I have outstanding finance?',
          link: 'https://www.zuto.com/help-and-advice/change-car-outstanding-finance/',
          target: '_self'
        }
      ]
    }
  }
]

const CarImageWrapper = styled(Image)(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    margintop: '-50px',
    width: '257px',
    height: '120px'
  }
}))

const PageContent = () => {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('mdplus'))
  return (
    <>
      <GlobalStyles />
      <BodyFullWidthContainer bgColor={colors.lightBlue}>
        <Grid
          component="section"
          container
          direction="row-reverse"
          sx={{
            justifyContent: { xs: 'center', mdplus: 'right' }
          }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '32px', sm: '50px' },
                mb: { xs: '24px', lg: 3 },
                marginTop: { xs: 5, lg: 0 }
              }}
            >
              How can we help?
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            mdplus={4}
            mb={3}
            sx={{
              justifyContent: { xs: 'center', mdplus: 'right' }
            }}
          >
            <CarImageWrapper
              src={`https://cdn.zuto.cloud/assets/images/help-advice-car.svg`}
              alt="Have a question about car finance?"
              width="320"
              height="150"
            />
          </Grid>
          <Grid
            item
            xs={12}
            mdplus={8}
            mb={3}
            sx={{ justifyContent: { xs: 'center', mdplus: 'left' } }}
          >
            <Typography variant="h3" component="p" sx={{ fontWeight: 400 }}>
              Have a question about car finance? You&apos;ll find everything you need to know here.
            </Typography>
            <div style={{ minHeight: '30px' }}>
              <TrustpilotRating
                id="tag_faq-tp-rating"
                style={{
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  justifyContent: matches ? 'left' : 'center'
                }}
              />
            </div>
          </Grid>
        </Grid>
        <Grid component="section" container>
          <Grid item xs={12} sx={{ marginTop: { xs: 2, mdplus: 3 } }}>
            <Typography variant="body1">
              Are you thinking about making a finance application and want to know what information
              is needed during the application process? Want to know what cars you can finance or
              whether you can get finance if you have bad credit? Then our{' '}
              <a role="link" aria-label="pre-application support" href="#pre-application-support">
                pre-application support
              </a>{' '}
              area is for you.
            </Typography>
            <Typography variant="body1">
              If you&apos;ve already made an application for{' '}
              <Link href="https://www.zuto.com/" aria-label="Zuto Homepage" role="link">
                car finance
              </Link>{' '}
              and want to know how long your quote is valid for, or what sort of things you can do
              in your account, visit{' '}
              <a
                role="link"
                aria-label="your application and account"
                href="#your-application-and-account"
              >
                your application and account
              </a>{' '}
              section.
            </Typography>
            <Typography variant="body1">
              Already taken out finance? If you require aftercare support for any problems or
              challenges, then our{' '}
              <a role="link" aria-label="Zuto customer service" href="#zuto-customer-service">
                Zuto customer service
              </a>{' '}
              area provides the support you need.
            </Typography>
          </Grid>
        </Grid>
        <Grid component="section" container>
          <CardGrid cardsData={cardsData} xsCols={12} mdCols={4} withBoxShadow />
        </Grid>
      </BodyFullWidthContainer>
    </>
  )
}

export default PageContent
